import React, { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SelectOption, SelectOptionForCustomDelivery } from "../../constants/index.js";
import OrderModal from "../../components/Modal/OrderModal.js";
import ConfirmationModal from "../../components/Modal/ConfirmationModal.jsx";
import Toast from "../../components/Toast/toast.js";
import { useAuth } from "../../utils/authContext.js";
import { useLanguage } from "../../utils/languageContext.js";
import { useDarkMode } from "../../utils/darkModeContext.js";
import { getTranslation } from "../../utils/translate.js";
import { useOrders, useOrderPolling } from "../../hooks/useOrders.js";
import { useScrollToTop } from "../../hooks/useScrollToTop.js";
import { getOrderStatusStyles } from "../../utils/orderStatusUtils.js";
import OrderTabs from "../../components/Order/OrderTabs.js";
import OngoingSubTabs from "../../components/Order/OngoingSubTabs.js";
import OrderList from "../../components/Order/OrderList.js";
import Pagination from "../../components/comman/pagination.js";
import RowsPerPageSelect from "../../components/comman/RowsPerPageSelect.js";
import Button from "../../components/comman/button.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import ErrorDisplay from "../../components/comman/ErrorDisplay.js";
import CustomMultiSelect from "../../components/comman/multi-select.js";
import KitchenSummary from "../../components/Order/KitchenSummary.js";
import NewOrdersBanner from "../../components/Order/NewOrdersBanner.js";
import Modal from "../../components/comman/modal.js";
import DoubleCalendar from "../../components/comman/dataPicker.js";
import calendarIcon from "../../assets/calendar.svg";
import companyService from "../../services/company-service.js";

const ORDERS_LIST_FILTERS_STORAGE_KEY = "bychef_kms_orders_list_filters";

const PAYMENT_STATUS_FILTER_VALUES = ["PAYED", "NOT_PAYED", "PENDING", "REFUNDED", "CANCELLED"];

const PAYMENT_DEFAULT_VALUES = ["PAYED", "NOT_PAYED"];

const pad2 = (n) => String(n).padStart(2, "0");

/** Preset delivery-time windows (local time) applied to the selected from/to dates for the API. */
const HISTORY_TIME_RANGE = {
    WHOLE_DAY: "WHOLE_DAY",
    T_00_10: "T_00_10",
    T_10_12: "T_10_12",
    T_12_13: "T_12_13",
    T_13_15: "T_13_15",
    T_15_16: "T_15_16",
    T_16_18: "T_16_18",
    T_18_00: "T_18_00"
};

/** Ongoing tab: local “today”, all ongoing (no dates in API), or a custom delivery date range. */
const ONGOING_DATE_SCOPE = {
    TODAY: "TODAY",
    ALL: "ALL",
    CUSTOM: "CUSTOM"
};

const formatDateOnly = (d) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** `dateStr` = YYYY-MM-DD; returns same format after adding whole days (local calendar). */
// Function addDaysToDateOnlyStr was removed as it is no longer used.

/** Mirror `handleHistoryQuickYesterday` for preset matching. */
const getHistoryQuickYesterdayBounds = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const s = formatDateOnly(d);
    return { from: s, to: s };
};

/** Mirror `handleHistoryQuickThisWeek` for preset matching. */
const getHistoryQuickThisWeekBounds = () => {
    const end = new Date();
    const start = new Date();
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);
    return { from: formatDateOnly(start), to: formatDateOnly(end) };
};

/** Mirror `handleHistoryQuickThisMonth` for preset matching. */
const getHistoryQuickThisMonthBounds = () => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    return { from: formatDateOnly(start), to: formatDateOnly(end) };
};

/**
 * Build `deliveryDateFrom` / `deliveryDateTo` for the backend from separate date + time-range pickers.
 * Uses a single contiguous window: first day at slot start → last day at slot end (local).
 */
const buildHistoryDeliveryIso = (dateFromStr, dateToStr, timeRange) => {
    if (!dateFromStr || !dateToStr || !timeRange) {
        return { deliveryDateFrom: null, deliveryDateTo: null };
    }
    const pf = dateFromStr.split("-").map(Number);
    const pt = dateToStr.split("-").map(Number);
    if (pf.length !== 3 || pt.length !== 3) {
        return { deliveryDateFrom: null, deliveryDateTo: null };
    }
    const [y1, mo1, d1] = pf;

    let startH = 0;
    let startM = 0;
    let startS = 0;
    let endH = 13;
    let endM = 1;
    let endS = 59;
    let endDateStr = dateToStr;

    if (timeRange === HISTORY_TIME_RANGE.WHOLE_DAY) {
        startH = 0;
        startM = 0;
        startS = 0;
        endH = 23;
        endM = 59;
        endS = 59;
        endDateStr = dateToStr;
    } else if (timeRange === HISTORY_TIME_RANGE.T_00_10) {
        startH = 0;
        startM = 0;
        startS = 0;
        endH = 9;
        endM = 59;
        endS = 59;
        endDateStr = dateToStr;
    } else if (timeRange === HISTORY_TIME_RANGE.T_10_12) {
        startH = 10;
        startM = 0;
        startS = 0;
        endH = 12;
        endM = 0;
        endS = 0;
        endDateStr = dateToStr;
    } else if (timeRange === HISTORY_TIME_RANGE.T_12_13) {
        startH = 12;
        startM = 0;
        startS = 0;
        endH = 13;
        endM = 0;
        endS = 0;
        endDateStr = dateToStr;
    } else if (timeRange === HISTORY_TIME_RANGE.T_13_15) {
        startH = 13;
        startM = 0;
        startS = 0;
        endH = 15;
        endM = 0;
        endS = 0;
        endDateStr = dateToStr;
    } else if (timeRange === HISTORY_TIME_RANGE.T_15_16) {
        startH = 15;
        startM = 0;
        startS = 0;
        endH = 16;
        endM = 0;
        endS = 0;
        endDateStr = dateToStr;
    } else if (timeRange === HISTORY_TIME_RANGE.T_16_18) {
        startH = 16;
        startM = 0;
        startS = 0;
        endH = 18;
        endM = 0;
        endS = 0;
        endDateStr = dateToStr;
    } else if (timeRange === HISTORY_TIME_RANGE.T_18_00) {
        startH = 18;
        startM = 0;
        startS = 0;
        endH = 23;
        endM = 59;
        endS = 59;
        endDateStr = dateToStr;
    }

    const pe = endDateStr.split("-").map(Number);
    if (pe.length !== 3) {
        return { deliveryDateFrom: null, deliveryDateTo: null };
    }
    const [y2, mo2, d2] = pe;

    const deliveryDateFrom = `${y1}-${pad2(mo1)}-${pad2(d1)}T${pad2(startH)}:${pad2(startM)}:${pad2(startS)}`;
    const deliveryDateTo = `${y2}-${pad2(mo2)}-${pad2(d2)}T${pad2(endH)}:${pad2(endM)}:${pad2(endS)}`;
    return { deliveryDateFrom, deliveryDateTo };
};

const getDefaultHistoryDateRange = () => {
    const to = new Date();
    const from = new Date(to);
    from.setMonth(from.getMonth() - 1);
    return {
        dateFrom: formatDateOnly(from),
        dateTo: formatDateOnly(to),
        timeRange: HISTORY_TIME_RANGE.WHOLE_DAY
    };
};

/** Migrate old localStorage (full ISO datetimes) or partial data */
const normalizeHistorySnapshot = (slice) => {
    if (!slice || typeof slice !== "object") {
        return getDefaultHistoryDateRange();
    }
    if (slice.dateFrom && slice.dateTo) {
        const tr = Object.values(HISTORY_TIME_RANGE).includes(slice.timeRange)
            ? slice.timeRange
            : HISTORY_TIME_RANGE.WHOLE_DAY;
        return {
            dateFrom: slice.dateFrom,
            dateTo: slice.dateTo,
            timeRange: tr
        };
    }
    if (slice.deliveryDateFrom && slice.deliveryDateTo) {
        const df = new Date(slice.deliveryDateFrom);
        const dt = new Date(slice.deliveryDateTo);
        if (!Number.isNaN(df.getTime()) && !Number.isNaN(dt.getTime())) {
            return {
                dateFrom: formatDateOnly(df),
                dateTo: formatDateOnly(dt),
                timeRange: HISTORY_TIME_RANGE.WHOLE_DAY
            };
        }
    }
    return getDefaultHistoryDateRange();
};

const isDefaultPaymentSelection = (values) => {
    if (!values || values.length === 0) return true;
    if (values.length !== PAYMENT_DEFAULT_VALUES.length) return false;
    const set = new Set(values.map((v) => (typeof v === "string" ? v : v.value)));
    return PAYMENT_DEFAULT_VALUES.every((x) => set.has(x));
};

const emptyFilterSnapshot = () => ({
    ongoing: {
        statusValues: [],
        companyValues: [],
        paymentStatusValues: [],
        ongoingSubTab: "orders",
        timeRange: HISTORY_TIME_RANGE.WHOLE_DAY,
        dateScope: ONGOING_DATE_SCOPE.TODAY,
        dateFrom: null,
        dateTo: null,
        orderNumber: ""
    },
    history: {
        statusValues: [],
        companyValues: [],
        paymentStatusValues: [],
        dateFrom: null,
        dateTo: null,
        timeRange: HISTORY_TIME_RANGE.WHOLE_DAY,
        orderNumber: ""
    }
});

const loadFiltersSnapshot = () => {
    try {
        const raw = localStorage.getItem(ORDERS_LIST_FILTERS_STORAGE_KEY);
        if (!raw) return emptyFilterSnapshot();
        const parsed = JSON.parse(raw);
        const base = emptyFilterSnapshot();
        const ongoingMerged = { ...base.ongoing, ...parsed.ongoing };
        if (parsed.dishes && typeof parsed.dishes === "object") {
            if (
                (!ongoingMerged.companyValues || ongoingMerged.companyValues.length === 0) &&
                Array.isArray(parsed.dishes.companyValues) &&
                parsed.dishes.companyValues.length > 0
            ) {
                ongoingMerged.companyValues = parsed.dishes.companyValues;
            }
        }
        if (!["orders", "dishes"].includes(ongoingMerged.ongoingSubTab)) {
            ongoingMerged.ongoingSubTab = "orders";
        }
        if (!Object.values(ONGOING_DATE_SCOPE).includes(ongoingMerged.dateScope)) {
            ongoingMerged.dateScope = ONGOING_DATE_SCOPE.TODAY;
        }
        if (
            ongoingMerged.dateScope === ONGOING_DATE_SCOPE.CUSTOM &&
            (!ongoingMerged.dateFrom || !ongoingMerged.dateTo)
        ) {
            ongoingMerged.dateScope = ONGOING_DATE_SCOPE.TODAY;
            ongoingMerged.dateFrom = null;
            ongoingMerged.dateTo = null;
        }
        return {
            ...base,
            ongoing: ongoingMerged,
            history: { ...base.history, ...parsed.history }
        };
    } catch {
        return emptyFilterSnapshot();
    }
};

const DEFAULT_ITEMS_PER_PAGE = { ongoing: 50, history: 20 };

const saveFiltersSnapshot = (snapshot) => {
    try {
        localStorage.setItem(ORDERS_LIST_FILTERS_STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
        // ignore quota / private mode
    }
};

const getInitialOngoingDateState = () => {
    const o = loadFiltersSnapshot().ongoing || {};
    if (o.dateScope === ONGOING_DATE_SCOPE.CUSTOM && o.dateFrom && o.dateTo) {
        return { scope: ONGOING_DATE_SCOPE.CUSTOM, from: o.dateFrom, to: o.dateTo };
    }
    if (o.dateScope === ONGOING_DATE_SCOPE.ALL) {
        return { scope: ONGOING_DATE_SCOPE.ALL, from: null, to: null };
    }
    return { scope: ONGOING_DATE_SCOPE.TODAY, from: null, to: null };
};

const ResetFiltersButton = ({ onClick, label }) => (
    <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onClick}
        className="gap-1.5 px-2 sm:px-3 text-gray-600 dark:text-[#8b949e] hover:text-gray-900 dark:hover:text-[#c9d1d9] font-medium"
        aria-label={label}
    >
        <svg
            className="w-4 h-4 shrink-0 opacity-90"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
        </svg>
        <span className="whitespace-nowrap">{label}</span>
    </Button>
);

const OrderNumberSearch = ({ value, onChange, label, placeholder }) => (
    <div className="w-full min-w-0">
        <label
            htmlFor="kms-order-number-search"
            className="block mb-2 text-sm font-bold text-fontColor dark:text-[#c9d1d9]"
        >
            {label}
        </label>
        <input
            id="kms-order-number-search"
            type="search"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            autoComplete="off"
            className="box-border h-10 min-h-10 px-3 w-full bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] placeholder:text-[14px] placeholder:text-gray-400 dark:placeholder:text-[#6e7681] text-sm border border-gray-200 dark:border-[#30363d] rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400"
        />
    </div>
);

const Orders = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { chefData } = useAuth();
    const { language } = useLanguage();
    const { isDarkMode } = useDarkMode();

    // Tab state — legacy `activeTab: "dishes"` opens ongoing + kitchen segment
    const [activeTab, setActiveTab] = useState(() =>
        location.state?.activeTab === "dishes" ? "ongoing" : location.state?.activeTab || "ongoing"
    );
    const [ongoingSubTab, setOngoingSubTab] = useState(() => {
        if (location.state?.activeTab === "dishes") return "dishes";
        const s = location.state?.ongoingSubTab;
        return s === "dishes" || s === "orders" ? s : "orders";
    });

    // Modal states
    const [isModalVisible, setModalVisible] = useState(false);
    const [isOrderModalVisible, setOrderModalVisible] = useState(false);
    const [declinedOrderId, setDeclinedOrderId] = useState(null);

    // Status change tracking
    const [selectedOptions, setSelectedOptions] = useState({});
    const [previousOptions, setPreviousOptions] = useState({});

    // Toast ref
    const toastRef = useRef(null);

    const filtersHydratedRef = useRef(false);
    const restoreFiltersForTabRef = useRef(null);

    // Filter state
    const [statusFilter, setStatusFilter] = useState([]);
    const [paymentStatusFilter, setPaymentStatusFilter] = useState([]);
    const [companyFilter, setCompanyFilter] = useState([]);
    const [historyDateFrom, setHistoryDateFrom] = useState(null);
    const [historyDateTo, setHistoryDateTo] = useState(null);
    const [historyTimeRange, setHistoryTimeRange] = useState(HISTORY_TIME_RANGE.WHOLE_DAY);
    const [historyDeliveryDatesDirty, setHistoryDeliveryDatesDirty] = useState(false);
    const [ongoingTimeRange, setOngoingTimeRange] = useState(HISTORY_TIME_RANGE.WHOLE_DAY);
    const [ongoingDateScope, setOngoingDateScope] = useState(() => getInitialOngoingDateState().scope);
    const [ongoingDateFrom, setOngoingDateFrom] = useState(() => getInitialOngoingDateState().from);
    const [ongoingDateTo, setOngoingDateTo] = useState(() => getInitialOngoingDateState().to);
    const [ongoingDatePickerOpen, setOngoingDatePickerOpen] = useState(false);
    const [historyDatePickerOpen, setHistoryDatePickerOpen] = useState(false);
    const [historyListFiltersModalOpen, setHistoryListFiltersModalOpen] = useState(false);
    const [draftStatusFilter, setDraftStatusFilter] = useState([]);
    const [draftPaymentStatusFilter, setDraftPaymentStatusFilter] = useState([]);
    const [draftCompanyFilter, setDraftCompanyFilter] = useState([]);
    const [orderNumberInput, setOrderNumberInput] = useState("");
    const [orderNumberFilter, setOrderNumberFilter] = useState("");

    const [partnerCompanies, setPartnerCompanies] = useState([]);

    useEffect(() => {
        const t = setTimeout(() => setOrderNumberFilter(orderNumberInput.trim()), 350);
        return () => clearTimeout(t);
    }, [orderNumberInput]);

    useEffect(() => {
        let cancelled = false;
        companyService
            .getAllCompanies()
            .then((data) => {
                if (!cancelled && Array.isArray(data)) {
                    setPartnerCompanies(data);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPartnerCompanies([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const historyDeliveryBounds = useMemo(
        () => buildHistoryDeliveryIso(historyDateFrom, historyDateTo, historyTimeRange),
        [historyDateFrom, historyDateTo, historyTimeRange]
    );

    const ongoingDeliveryBounds = useMemo(() => {
        if (ongoingDateScope === ONGOING_DATE_SCOPE.ALL) {
            return { deliveryDateFrom: null, deliveryDateTo: null };
        }
        const hasCustomDates =
            ongoingDateScope === ONGOING_DATE_SCOPE.CUSTOM && ongoingDateFrom && ongoingDateTo;
        const from = hasCustomDates ? ongoingDateFrom : formatDateOnly(new Date());
        const to = hasCustomDates ? ongoingDateTo : formatDateOnly(new Date());
        return buildHistoryDeliveryIso(from, to, ongoingTimeRange);
    }, [ongoingDateScope, ongoingDateFrom, ongoingDateTo, ongoingTimeRange]);

    /** Kitchen dish row highlights: scoped so changing ongoing date/time filters does not reuse another window’s marks. */
    const ongoingKitchenHighlightSliceId = useMemo(
        () =>
            [
                ongoingDateScope,
                ongoingTimeRange,
                ongoingDeliveryBounds.deliveryDateFrom ?? "",
                ongoingDeliveryBounds.deliveryDateTo ?? ""
            ].join("\u001f"),
        [
            ongoingDateScope,
            ongoingTimeRange,
            ongoingDeliveryBounds.deliveryDateFrom,
            ongoingDeliveryBounds.deliveryDateTo
        ]
    );

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPageByTab, setItemsPerPageByTab] = useState(() => ({ ...DEFAULT_ITEMS_PER_PAGE }));
    const itemsPerPage = itemsPerPageByTab[activeTab];

    const statusFilterValues = useMemo(() => statusFilter.map((o) => o.value), [statusFilter]);

    const paymentStatusFilterValues = useMemo(
        () => paymentStatusFilter.map((o) => o.value),
        [paymentStatusFilter]
    );

    const companyFilterValues = useMemo(() => companyFilter.map((o) => o.value), [companyFilter]);

    /** Remount kitchen dish list when any ongoing list UI input changes so row colors stay ephemeral. */
    const kitchenHighlightMountKey = useMemo(
        () =>
            [
                ongoingKitchenHighlightSliceId,
                String(chefData?.id ?? ""),
                statusFilterValues.slice().sort().join(","),
                companyFilterValues.slice().sort().join(","),
                String(currentPage),
                String(itemsPerPage),
                orderNumberFilter,
                ongoingSubTab
            ].join("\u001f"),
        [
            ongoingKitchenHighlightSliceId,
            chefData?.id,
            statusFilterValues,
            companyFilterValues,
            currentPage,
            itemsPerPage,
            orderNumberFilter,
            ongoingSubTab
        ]
    );

    // Custom hooks
    const {
        orderData,
        totalCount,
        loading,
        refreshing,
        error,
        newOrdersCount,
        fetchOrders,
        checkForNewOrders,
        updateStatus
    } = useOrders({
        chefId: chefData?.id,
        activeTab,
        currentPage,
        itemsPerPage,
        toastRef,
        statusFilterValues,
        paymentStatusFilterValues,
        companyFilterValues,
        orderNumber: orderNumberFilter,
        deliveryDateFrom:
            activeTab === "history"
                ? historyDeliveryBounds.deliveryDateFrom
                : ongoingDeliveryBounds.deliveryDateFrom,
        deliveryDateTo:
            activeTab === "history"
                ? historyDeliveryBounds.deliveryDateTo
                : ongoingDeliveryBounds.deliveryDateTo
    });

    const { showScrollTop, scrollToTop } = useScrollToTop(300);

    // Polling only checks for new orders; the list is reloaded when the user clicks Refresh
    useOrderPolling(checkForNewOrders, activeTab);
    
    // Get translated select options
    const getTranslatedSelectOptions = useCallback(() => {
        // Check first order's delivery method if available
        const firstOrder = orderData && orderData.length > 0 ? orderData[0] : null;
        const deliveryMethod = firstOrder?.deliveryMethod;
        const options = deliveryMethod !== "YANDEX" ? SelectOptionForCustomDelivery : SelectOption;
        
        return options.map(option => ({
            ...option,
            label: getTranslation(`orderStatuses.${option.value.toLowerCase()}`, language)
        }));
    }, [orderData, language]);

    const selectOptions = useMemo(() => getTranslatedSelectOptions(), [getTranslatedSelectOptions]);

    const ongoingFilterOptions = useMemo(() => [
        { value: "REQUESTED", label: getTranslation("orderStatuses.requested", language) },
        { value: "PREPARING", label: getTranslation("orderStatuses.preparing", language) },
        { value: "READY_FOR_DELIVERY", label: getTranslation("orderStatuses.ready_for_delivery", language) },
        { value: "DELIVERY_IN_PROGRESS", label: getTranslation("orderStatuses.delivery_in_progress", language) },
    ], [language]);

    const historyFilterOptions = useMemo(() => [
        { value: "DELIVERED", label: getTranslation("orderStatuses.delivered", language) },
        { value: "DECLINED", label: getTranslation("orderStatuses.declined", language) },
    ], [language]);

    const paymentFilterOptions = useMemo(
        () =>
            PAYMENT_STATUS_FILTER_VALUES.map((value) => ({
                value,
                label: getTranslation(`paymentStatuses.${value.toLowerCase()}`, language) || value,
            })),
        [language]
    );

    const companyFilterOptions = useMemo(() => {
        const seen = new Set();
        const options = [];
        partnerCompanies.forEach((company) => {
            const id = company?.id;
            const name = (company?.name || "").trim();
            if (id == null || !name || seen.has(id)) {
                return;
            }
            seen.add(id);
            options.push({ value: String(id), label: name });
        });
        return options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    }, [partnerCompanies]);

    const showCompanyFilter = companyFilterOptions.length > 0;

    const restoreFiltersForTab = useCallback(
        (tab) => {
            const snap = loadFiltersSnapshot();
            const slice = snap[tab] || { statusValues: [], companyValues: [], paymentStatusValues: [] };
            const statusPool = tab === "ongoing" ? ongoingFilterOptions : historyFilterOptions;
            const allowed = new Set(statusPool.map((o) => o.value));
            const statusValues = (slice.statusValues || []).filter((v) => allowed.has(v));
            const status = statusValues.map((v) => {
                const opt = statusPool.find((o) => o.value === v);
                return opt || { value: v, label: v };
            });
            const company = (slice.companyValues || [])
                .map((v) => companyFilterOptions.find((o) => o.value === String(v)))
                .filter(Boolean);
            const allowedPayment = new Set(PAYMENT_STATUS_FILTER_VALUES);
            let paymentValues = (slice.paymentStatusValues || []).filter((v) => allowedPayment.has(v));
            if (tab === "history" && paymentValues.length === 0) {
                paymentValues = [...PAYMENT_DEFAULT_VALUES];
            }
            const payment = paymentValues.map((v) => {
                const opt = paymentFilterOptions.find((o) => o.value === v);
                return opt || { value: v, label: v };
            });
            const number = typeof slice.orderNumber === "string" ? slice.orderNumber : "";
            setOrderNumberInput(number);
            setOrderNumberFilter(number.trim());
            setHistoryDeliveryDatesDirty(false);
            if (tab === "history") {
                setStatusFilter(status);
                setPaymentStatusFilter(payment);
                setCompanyFilter(company);
                const h = normalizeHistorySnapshot(slice);
                setHistoryDateFrom(h.dateFrom);
                setHistoryDateTo(h.dateTo);
                setHistoryTimeRange(h.timeRange);
            } else {
                /* ongoing: optional status + company filters; empty status = API default (all ongoing statuses) */
                setStatusFilter(status);
                setPaymentStatusFilter([]);
                setCompanyFilter(company);
                const tr = Object.values(HISTORY_TIME_RANGE).includes(slice.timeRange)
                    ? slice.timeRange
                    : HISTORY_TIME_RANGE.WHOLE_DAY;
                setOngoingTimeRange(tr);
                if (
                    slice.dateScope === ONGOING_DATE_SCOPE.CUSTOM &&
                    slice.dateFrom &&
                    slice.dateTo
                ) {
                    setOngoingDateScope(ONGOING_DATE_SCOPE.CUSTOM);
                    setOngoingDateFrom(slice.dateFrom);
                    setOngoingDateTo(slice.dateTo);
                } else {
                    setOngoingDateScope(
                        slice.dateScope === ONGOING_DATE_SCOPE.ALL
                            ? ONGOING_DATE_SCOPE.ALL
                            : ONGOING_DATE_SCOPE.TODAY
                    );
                    setOngoingDateFrom(null);
                    setOngoingDateTo(null);
                }
                setHistoryDateFrom(null);
                setHistoryDateTo(null);
                setHistoryTimeRange(HISTORY_TIME_RANGE.WHOLE_DAY);
                const sub = slice.ongoingSubTab === "dishes" || slice.ongoingSubTab === "orders" ? slice.ongoingSubTab : "orders";
                setOngoingSubTab(sub);
            }
        },
        [ongoingFilterOptions, historyFilterOptions, paymentFilterOptions, companyFilterOptions]
    );

    restoreFiltersForTabRef.current = restoreFiltersForTab;

    // Get option styles
    const getOptionStyles = useCallback(
        (option) => getOrderStatusStyles(option, isDarkMode),
        [isDarkMode]
    );

    const historyThreeFieldFiltersActive = useMemo(() => {
        if (statusFilter.length > 0) return true;
        if (companyFilter.length > 0) return true;
        if (orderNumberInput.trim() !== "") return true;
        if (!isDefaultPaymentSelection(paymentStatusFilter)) return true;
        return false;
    }, [statusFilter, companyFilter, paymentStatusFilter, orderNumberInput]);

    const hasActiveListFilters = useMemo(() => {
        if (activeTab === "ongoing") {
            return (
                companyFilter.length > 0 ||
                statusFilter.length > 0 ||
                orderNumberInput.trim() !== "" ||
                ongoingTimeRange !== HISTORY_TIME_RANGE.WHOLE_DAY ||
                ongoingDateScope !== ONGOING_DATE_SCOPE.TODAY
            );
        }
        if (historyThreeFieldFiltersActive) return true;
        if (activeTab === "history" && historyDeliveryDatesDirty) return true;
        return false;
    }, [
        activeTab,
        companyFilter.length,
        statusFilter.length,
        historyThreeFieldFiltersActive,
        historyDeliveryDatesDirty,
        ongoingTimeRange,
        ongoingDateScope,
        orderNumberInput
    ]);

    const totalPages = useMemo(
        () => Math.ceil(totalCount / itemsPerPage) || 1,
        [totalCount, itemsPerPage]
    );

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(1);
        }
    }, [currentPage, totalPages]);

    const listFilterResetKey = [
        activeTab,
        statusFilterValues.join(","),
        companyFilterValues.join("\u001f"),
        paymentStatusFilterValues.join(","),
        orderNumberFilter,
        historyDateFrom,
        historyDateTo,
        historyTimeRange,
        ongoingTimeRange,
        ongoingDateScope,
        ongoingDateFrom,
        ongoingDateTo
    ].join("|");

    // Reset pagination when tab or server-side filters change (ongoing sub-tab uses same fetch; no reset on switch)
    useEffect(() => {
        setCurrentPage((page) => (page === 1 ? page : 1));
    }, [listFilterResetKey]);

    // Restore filters from localStorage only when chef or tab changes — not when option labels refresh,
    // or persist/hydrate fight (Today ↔ All) and refetch forever.
    useLayoutEffect(() => {
        if (!chefData?.id) {
            filtersHydratedRef.current = false;
            return;
        }
        restoreFiltersForTabRef.current?.(activeTab);
        filtersHydratedRef.current = true;
    }, [chefData?.id, activeTab]);

    // Persist filters per tab
    useEffect(() => {
        if (!chefData?.id || !filtersHydratedRef.current) return;
        const snap = loadFiltersSnapshot();
        if (activeTab === "history") {
            snap.history = {
                statusValues: statusFilter.map((o) => o.value),
                companyValues: companyFilter.map((o) => o.value),
                paymentStatusValues: paymentStatusFilter.map((o) => o.value),
                dateFrom: historyDateFrom,
                dateTo: historyDateTo,
                timeRange: historyTimeRange,
                orderNumber: orderNumberInput
            };
        } else {
            snap.ongoing = {
                statusValues: statusFilter.map((o) => o.value),
                companyValues: companyFilter.map((o) => o.value),
                paymentStatusValues: [],
                ongoingSubTab,
                timeRange: ongoingTimeRange,
                dateScope: ongoingDateScope,
                dateFrom: ongoingDateScope === ONGOING_DATE_SCOPE.CUSTOM ? ongoingDateFrom : null,
                dateTo: ongoingDateScope === ONGOING_DATE_SCOPE.CUSTOM ? ongoingDateTo : null,
                orderNumber: orderNumberInput
            };
        }
        saveFiltersSnapshot(snap);
    }, [
        chefData?.id,
        activeTab,
        statusFilter,
        companyFilter,
        paymentStatusFilter,
        orderNumberInput,
        historyDateFrom,
        historyDateTo,
        historyTimeRange,
        ongoingSubTab,
        ongoingTimeRange,
        ongoingDateScope,
        ongoingDateFrom,
        ongoingDateTo
    ]);

    const setItemsPerPageAndReset = useCallback((value) => {
        setItemsPerPageByTab((prev) => ({ ...prev, [activeTab]: value }));
        setCurrentPage(1);
    }, [activeTab]);

    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
    }, []);

    const handleOngoingSubTabChange = useCallback((sub) => {
        setOngoingSubTab(sub);
    }, []);

    const applyOngoingDateScope = useCallback(
        (scope) => {
            setOngoingDateScope(scope);
            if (scope !== ONGOING_DATE_SCOPE.CUSTOM) {
                setOngoingDateFrom(null);
                setOngoingDateTo(null);
            }
            const snap = loadFiltersSnapshot();
            snap.ongoing = {
                statusValues: statusFilter.map((o) => o.value),
                companyValues: companyFilter.map((o) => o.value),
                paymentStatusValues: [],
                ongoingSubTab,
                timeRange: ongoingTimeRange,
                dateScope: scope,
                dateFrom: null,
                dateTo: null,
                orderNumber: orderNumberInput
            };
            saveFiltersSnapshot(snap);
        },
        [statusFilter, companyFilter, ongoingSubTab, ongoingTimeRange, orderNumberInput]
    );

    /** Restore all filters for the active tab to their default values (not empty “clear”). */
    const handleResetFilters = useCallback(() => {
        if (activeTab === "ongoing") {
            setCompanyFilter([]);
            setStatusFilter([]);
            setOrderNumberInput("");
            setOrderNumberFilter("");
            setOngoingTimeRange(HISTORY_TIME_RANGE.WHOLE_DAY);
            setOngoingDateScope(ONGOING_DATE_SCOPE.TODAY);
            setOngoingDateFrom(null);
            setOngoingDateTo(null);
            setCurrentPage(1);
            return;
        }
        setStatusFilter([]);
        setCompanyFilter([]);
        setOrderNumberInput("");
        setOrderNumberFilter("");
        setHistoryDeliveryDatesDirty(false);
        setPaymentStatusFilter(paymentFilterOptions.filter((o) => PAYMENT_DEFAULT_VALUES.includes(o.value)));
        const d = getDefaultHistoryDateRange();
        setHistoryDateFrom(d.dateFrom);
        setHistoryDateTo(d.dateTo);
        setHistoryTimeRange(d.timeRange);
        setCurrentPage(1);
    }, [activeTab, paymentFilterOptions]);

    const handleStatusUpdate = useCallback(
        async (orderId, status) => {
            try {
                await updateStatus(orderId, status);
        } catch (error) {
                // Rollback on error
                setSelectedOptions((prev) => ({
                    ...prev,
                    [orderId]: previousOptions[orderId]
                }));
            }
        },
        [updateStatus, previousOptions]
    );

    const handleSelectChange = useCallback(
        async (orderId, option) => {
            // Store previous option for rollback
            const currentOrder = orderData.find((order) => order.id === orderId);
            setPreviousOptions((prev) => ({
                ...prev,
                [orderId]: selectedOptions[orderId] || selectOptions.find((opt) => opt.value === currentOrder?.status)
            }));
            
            setSelectedOptions((prev) => ({
                ...prev,
                [orderId]: option
            }));

            if (option.value === "DECLINED") {
                setModalVisible(true);
                setDeclinedOrderId(orderId);
                return;
            }

            await handleStatusUpdate(orderId, option.value);
        },
        [orderData, selectedOptions, selectOptions, handleStatusUpdate]
    );

    const handleViewOrder = useCallback(
        (order) => {
            const fromTab =
                activeTab === "ongoing" && ongoingSubTab === "dishes" ? "dishes" : activeTab;
            const state = {
                fromTab,
                ...(activeTab === "ongoing" ? { ongoingSubTab } : {})
            };

            const src = order?.source;
            const isPersisted = order?.id != null;
            const fromOrderNumber =
                order?.orderNumber != null && String(order.orderNumber).trim() !== ""
                    ? String(order.orderNumber).trim()
                    : "";
            const fromDisplayNumber =
                order?.number != null && String(order.number).trim() !== ""
                    ? String(order.number).trim()
                    : "";
            const externalKey = fromOrderNumber || fromDisplayNumber;
            const isLiveExternal = !isPersisted && src != null && src !== "BYCHEF" && externalKey !== "";

            if (isLiveExternal) {
                const qs = new URLSearchParams({ source: String(src) }).toString();
                navigate(`/orders/external/${encodeURIComponent(externalKey)}?${qs}`, {
                    state: { ...state, source: String(src) }
                });
                return;
            }

            if (!order?.id) return;
            navigate(`/orders/${order.id}`, { state });
        },
        [navigate, activeTab, ongoingSubTab]
    );

    const handleConfirmDecline = useCallback(() => {
        setModalVisible(false);
        setOrderModalVisible(true);
    }, []);

    const handleCancelDecline = useCallback(() => {
        // Rollback to previous status
        setSelectedOptions((prev) => ({
            ...prev,
            [declinedOrderId]: previousOptions[declinedOrderId]
        }));
        setModalVisible(false);
    }, [declinedOrderId, previousOptions]);

    const handleCloseOrderModal = useCallback(() => {
        setSelectedOptions((prev) => ({
            ...prev,
            [declinedOrderId]: previousOptions[declinedOrderId]
        }));
        fetchOrders({ force: true }).then(() => setOrderModalVisible(false));
    }, [declinedOrderId, previousOptions, fetchOrders]);

    const applyHistoryDateRangeStrings = useCallback((fromStr, toStr) => {
        setHistoryDeliveryDatesDirty(true);
        setHistoryDateFrom(fromStr || null);
        setHistoryDateTo(toStr || null);
    }, []);

    const handleHistoryCalendarSelect = useCallback(
        (selectedDates) => {
            if (selectedDates && selectedDates.length > 0) {
                const from = new Date(selectedDates[0]);
                const to = new Date(selectedDates[selectedDates.length - 1]);
                applyHistoryDateRangeStrings(formatDateOnly(from), formatDateOnly(to));
                setHistoryDatePickerOpen(false);
            }
        },
        [applyHistoryDateRangeStrings]
    );

    const handleOngoingCalendarSelect = useCallback((selectedDates) => {
        if (selectedDates && selectedDates.length > 0) {
            const from = new Date(selectedDates[0]);
            const to = new Date(selectedDates[selectedDates.length - 1]);
            setOngoingDateFrom(formatDateOnly(from));
            setOngoingDateTo(formatDateOnly(to));
            setOngoingDateScope(ONGOING_DATE_SCOPE.CUSTOM);
            setOngoingDatePickerOpen(false);
        }
    }, []);

    const handleHistoryQuickToday = useCallback(() => {
        const d = new Date();
        applyHistoryDateRangeStrings(formatDateOnly(d), formatDateOnly(d));
    }, [applyHistoryDateRangeStrings]);

    const handleHistoryQuickYesterday = useCallback(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        applyHistoryDateRangeStrings(formatDateOnly(d), formatDateOnly(d));
    }, [applyHistoryDateRangeStrings]);

    const handleHistoryQuickThisWeek = useCallback(() => {
        const end = new Date();
        const start = new Date();
        const day = start.getDay();
        const diff = start.getDate() - day + (day === 0 ? -6 : 1);
        start.setDate(diff);
        applyHistoryDateRangeStrings(formatDateOnly(start), formatDateOnly(end));
    }, [applyHistoryDateRangeStrings]);

    const handleHistoryQuickThisMonth = useCallback(() => {
        const end = new Date();
        const start = new Date(end.getFullYear(), end.getMonth(), 1);
        applyHistoryDateRangeStrings(formatDateOnly(start), formatDateOnly(end));
    }, [applyHistoryDateRangeStrings]);

    const historyQuickPresetFlags = useMemo(() => {
        if (!historyDateFrom || !historyDateTo) {
            return { today: false, yesterday: false, thisWeek: false, thisMonth: false };
        }
        const todayStr = formatDateOnly(new Date());
        const yesterday = getHistoryQuickYesterdayBounds();
        const week = getHistoryQuickThisWeekBounds();
        const month = getHistoryQuickThisMonthBounds();
        return {
            today: historyDateFrom === todayStr && historyDateTo === todayStr,
            yesterday: historyDateFrom === yesterday.from && historyDateTo === yesterday.to,
            thisWeek: historyDateFrom === week.from && historyDateTo === week.to,
            thisMonth: historyDateFrom === month.from && historyDateTo === month.to
        };
    }, [historyDateFrom, historyDateTo]);

    const quickDateActiveRing = "ring-2 ring-gray-400/40 dark:ring-[#58a6ff]/35";

    const openHistoryListFiltersModal = useCallback(() => {
        setDraftStatusFilter(statusFilter);
        setDraftPaymentStatusFilter(paymentStatusFilter);
        setDraftCompanyFilter(companyFilter);
        setHistoryListFiltersModalOpen(true);
    }, [statusFilter, paymentStatusFilter, companyFilter]);

    const applyHistoryListFilters = useCallback(() => {
        setStatusFilter(draftStatusFilter);
        setPaymentStatusFilter(draftPaymentStatusFilter);
        setCompanyFilter(draftCompanyFilter);
        setHistoryListFiltersModalOpen(false);
    }, [draftStatusFilter, draftPaymentStatusFilter, draftCompanyFilter]);

    /** Reset list filters (status / payment / company) to defaults and apply immediately — no separate Apply click. */
    const resetAndApplyHistoryListFilters = useCallback(() => {
        const defaultPayment = paymentFilterOptions.filter((o) =>
            PAYMENT_DEFAULT_VALUES.includes(o.value)
        );
        setStatusFilter([]);
        setCompanyFilter([]);
        setPaymentStatusFilter(defaultPayment);
        setDraftStatusFilter([]);
        setDraftCompanyFilter([]);
        setDraftPaymentStatusFilter(defaultPayment);
        setHistoryListFiltersModalOpen(false);
    }, [paymentFilterOptions]);

    const closeHistoryListFiltersModal = useCallback(() => {
        setHistoryListFiltersModalOpen(false);
    }, []);

    const historyCalendarInitialFrom = useMemo(
        () => (historyDateFrom ? new Date(`${historyDateFrom}T12:00:00`) : new Date()),
        [historyDateFrom]
    );

    const historyCalendarInitialTo = useMemo(
        () =>
            historyDateTo
                ? new Date(`${historyDateTo}T12:00:00`)
                : historyCalendarInitialFrom,
        [historyDateTo, historyCalendarInitialFrom]
    );

    const ongoingCalendarInitialFrom = useMemo(
        () => (ongoingDateFrom ? new Date(`${ongoingDateFrom}T12:00:00`) : new Date()),
        [ongoingDateFrom]
    );

    const ongoingCalendarInitialTo = useMemo(
        () =>
            ongoingDateTo
                ? new Date(`${ongoingDateTo}T12:00:00`)
                : ongoingCalendarInitialFrom,
        [ongoingDateTo, ongoingCalendarInitialFrom]
    );

    const ongoingCustomDateLabel = useMemo(() => {
        if (ongoingDateScope !== ONGOING_DATE_SCOPE.CUSTOM || !ongoingDateFrom) {
            return getTranslation("payments.selectDate", language);
        }
        return ongoingDateTo && ongoingDateTo !== ongoingDateFrom
            ? `${ongoingDateFrom} – ${ongoingDateTo}`
            : ongoingDateFrom;
    }, [ongoingDateScope, ongoingDateFrom, ongoingDateTo, language]);

    const handleHistoryTimeRangeChange = useCallback((rangeId) => {
        setHistoryDeliveryDatesDirty(true);
        setHistoryTimeRange(rangeId);
    }, []);

    const historyTimeRangeOptions = useMemo(
        () => [
            { id: HISTORY_TIME_RANGE.WHOLE_DAY, label: getTranslation("orderPage.timeRangeWholeDay", language) },
            // { id: HISTORY_TIME_RANGE.T_00_10, label: "00:00 - 10:00" },
            { id: HISTORY_TIME_RANGE.T_10_12, label: "10:00 - 12:00" },
            { id: HISTORY_TIME_RANGE.T_12_13, label: "12:00 - 13:00" },
            { id: HISTORY_TIME_RANGE.T_13_15, label: "13:00 - 15:00" },
            { id: HISTORY_TIME_RANGE.T_15_16, label: "15:00 - 16:00" },
            { id: HISTORY_TIME_RANGE.T_16_18, label: "16:00 - 18:00" },
            { id: HISTORY_TIME_RANGE.T_18_00, label: "18:00 - 00:00" }
        ],
        [language]
    );

    const resetFiltersLabel = getTranslation("orderPage.resetFilters", language) || "Reset filters";
    const applyFiltersLabel = getTranslation("orderPage.applyFilters", language) || "Apply";
    const resetAllFiltersLabel = getTranslation("orderPage.resetAllFilters", language) || "Reset all";
    const listFiltersTitle = getTranslation("orderPage.listFiltersTitle", language) || "Filters";
    const openListFiltersLabel = getTranslation("orderPage.openListFilters", language) || "Open order filters";

    return (
        <div className="pl-2">
            <h1 className="text-xl font-bold mb-4 text-gray-900 dark:text-[#c9d1d9]">
                {getTranslation("menuItems.orders", language)}
            </h1>
            
            {/* Pull to refresh indicator */}
            {refreshing && (
                <div className="fixed left-0 right-0 top-0 z-20 flex justify-center items-center h-16 bg-white dark:bg-gray-900/95 transition-all duration-200">
                    <LoadingSpinner />
                    <span className="ml-2 text-sm text-gray-600 dark:text-[#b1bac4]">
                        {getTranslation("common.refreshing", language) || "Refreshing..."}
                    </span>
                </div>
            )}

            <OrderTabs activeTab={activeTab} onTabChange={handleTabChange} />

            {activeTab === "ongoing" && (
                <div className="mt-3 mb-1 px-1">
                    <OngoingSubTabs ongoingSubTab={ongoingSubTab} onChange={handleOngoingSubTabChange} />
                </div>
            )}

            <div
                className="mt-4 mb-5 px-2 sm:px-3 py-2.5 sm:py-3 rounded-xl border border-gray-200/90 dark:border-[#30363d] bg-gray-50/90 dark:bg-[#0d1117]/80 shadow-sm"
                role="region"
                aria-label={getTranslation("orderPage.filtersRegion", language)}
            >
                {activeTab === "ongoing" && (
                    <div className="space-y-3">
                        <div
                            className={`grid grid-cols-1 gap-3 sm:gap-4 items-end ${
                                showCompanyFilter ? "sm:grid-cols-3" : "sm:grid-cols-2"
                            }`}
                        >
                            <OrderNumberSearch
                                value={orderNumberInput}
                                onChange={setOrderNumberInput}
                                label={getTranslation("orderPage.orderNumber", language) || "Order Number"}
                                placeholder={
                                    getTranslation("orderPage.searchOrderNumber", language) || "Search by order number"
                                }
                            />
                            <div className="w-full min-w-0 z-10">
                                <CustomMultiSelect
                                    label={getTranslation("orderPage.orderStatusFilter", language) || "Order status"}
                                    options={ongoingFilterOptions}
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    margin=""
                                    placeholder={
                                        getTranslation("orderPage.allOrderStatuses", language) || "All statuses"
                                    }
                                    isRequired={false}
                                    showRequiredIndicator={false}
                                />
                            </div>
                            {showCompanyFilter && (
                            <div className="w-full min-w-0 z-10">
                                <CustomMultiSelect
                                    label={getTranslation("orderPage.company", language) || "Company"}
                                    options={companyFilterOptions}
                                    value={companyFilter}
                                    onChange={setCompanyFilter}
                                    margin=""
                                    placeholder={getTranslation("orderPage.allCompanies", language) || "All Companies"}
                                    isRequired={false}
                                    showRequiredIndicator={false}
                                />
                            </div>
                            )}
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 min-w-0 flex-1">
                                <span className="text-xs font-medium text-gray-600 dark:text-[#8b949e] shrink-0">
                                    {getTranslation("orderPage.ongoingDeliveryDate", language)}
                                </span>
                                <div
                                    className="flex flex-wrap gap-1 min-w-0"
                                    role="group"
                                    aria-label={getTranslation("orderPage.ongoingDeliveryDate", language)}
                                >
                                    <button
                                        key="ongoing-date-today"
                                        type="button"
                                        onClick={() => applyOngoingDateScope(ONGOING_DATE_SCOPE.TODAY)}
                                        className={`min-w-0 px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                                            ongoingDateScope === ONGOING_DATE_SCOPE.TODAY
                                                ? "bg-gray-900 text-white border-gray-900 dark:bg-[#30363d] dark:text-[#c9d1d9] dark:border-[#58a6ff]/50 ring-1 ring-gray-400/30 dark:ring-[#58a6ff]/25"
                                                : "bg-white dark:bg-[#161b22] text-gray-800 dark:text-[#c9d1d9] border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#21262d]"
                                        }`}
                                    >
                                        {getTranslation("common.today", language) || "Today"}
                                    </button>
                                    <button
                                        key="ongoing-date-all"
                                        type="button"
                                        onClick={() => applyOngoingDateScope(ONGOING_DATE_SCOPE.ALL)}
                                        className={`min-w-0 px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                                            ongoingDateScope === ONGOING_DATE_SCOPE.ALL
                                                ? "bg-gray-900 text-white border-gray-900 dark:bg-[#30363d] dark:text-[#c9d1d9] dark:border-[#58a6ff]/50 ring-1 ring-gray-400/30 dark:ring-[#58a6ff]/25"
                                                : "bg-white dark:bg-[#161b22] text-gray-800 dark:text-[#c9d1d9] border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#21262d]"
                                        }`}
                                    >
                                        {getTranslation("orderPage.ongoingDateScopeAll", language) || "All"}
                                    </button>
                                    <button
                                        key="ongoing-date-custom"
                                        type="button"
                                        onClick={() => setOngoingDatePickerOpen(true)}
                                        className={`inline-flex min-w-0 items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                                            ongoingDateScope === ONGOING_DATE_SCOPE.CUSTOM
                                                ? "bg-gray-900 text-white border-gray-900 dark:bg-[#30363d] dark:text-[#c9d1d9] dark:border-[#58a6ff]/50 ring-1 ring-gray-400/30 dark:ring-[#58a6ff]/25"
                                                : "bg-white dark:bg-[#161b22] text-gray-800 dark:text-[#c9d1d9] border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#21262d]"
                                        }`}
                                        aria-label={getTranslation("payments.selectDate", language)}
                                    >
                                        <img src={calendarIcon} alt="" className="h-3.5 w-3.5 shrink-0 opacity-80 dark:invert dark:opacity-80" />
                                        <span className="truncate tabular-nums">{ongoingCustomDateLabel}</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 min-w-0 flex-1">
                                <span className="text-xs font-medium text-gray-600 dark:text-[#8b949e] shrink-0">
                                    {getTranslation("orderPage.deliveryTimeRange", language)}
                                </span>
                                <div
                                    className="flex flex-wrap gap-1 min-w-0"
                                    role="group"
                                    aria-label={getTranslation("orderPage.deliveryTimeRange", language)}
                                >
                                    {historyTimeRangeOptions.map((opt) => {
                                        const timeDisabled = ongoingDateScope === ONGOING_DATE_SCOPE.ALL;
                                        return (
                                        <button
                                            key={`ongoing-${opt.id}`}
                                            type="button"
                                            disabled={timeDisabled}
                                            onClick={() => setOngoingTimeRange(opt.id)}
                                            className={`min-w-0 px-2 py-1 rounded-md text-xs font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                                ongoingTimeRange === opt.id
                                                    ? "bg-gray-900 text-white border-gray-900 dark:bg-[#30363d] dark:text-[#c9d1d9] dark:border-[#58a6ff]/50 ring-1 ring-gray-400/30 dark:ring-[#58a6ff]/25"
                                                    : "bg-white dark:bg-[#161b22] text-gray-800 dark:text-[#c9d1d9] border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#21262d]"
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                        );
                                    })}
                                </div>
                            </div>
                            {hasActiveListFilters && (
                                <div className="flex shrink-0 sm:justify-end">
                                    <ResetFiltersButton onClick={handleResetFilters} label={resetFiltersLabel} />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === "history" && (
                    <div className="space-y-3">
                        <OrderNumberSearch
                            value={orderNumberInput}
                            onChange={setOrderNumberInput}
                            label={getTranslation("orderPage.orderNumber", language) || "Order Number"}
                            placeholder={
                                getTranslation("orderPage.searchOrderNumber", language) || "Search by order number"
                            }
                        />
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
                                <button
                                    type="button"
                                    onClick={openHistoryListFiltersModal}
                                    className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#161b22] text-gray-800 dark:text-[#c9d1d9] shadow-sm hover:bg-gray-50 dark:hover:bg-[#21262d] transition-colors shrink-0"
                                    aria-label={openListFiltersLabel}
                                >
                                    <svg
                                        className="h-5 w-5 shrink-0"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        aria-hidden
                                    >
                                        <path
                                            d="M8 7h13M8 12h13M8 17h10"
                                            stroke="currentColor"
                                            strokeWidth="1.75"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <circle cx="4.5" cy="7" r="1.25" fill="currentColor" />
                                        <circle cx="4.5" cy="12" r="1.25" fill="currentColor" />
                                        <circle cx="4.5" cy="17" r="1.25" fill="currentColor" />
                                    </svg>
                                    {historyThreeFieldFiltersActive ? (
                                        <span
                                            className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-sky-500 ring-2 ring-white dark:ring-[#161b22]"
                                            aria-hidden
                                        />
                                    ) : null}
                                </button>
                                <span
                                    className="hidden sm:block h-7 w-px shrink-0 bg-gray-200 dark:bg-[#30363d]"
                                    aria-hidden
                                />
                                <Button
                                    variant={historyQuickPresetFlags.today ? "primary" : "secondary"}
                                    size="sm"
                                    type="button"
                                    onClick={handleHistoryQuickToday}
                                    className={`!py-1.5 text-xs ${historyQuickPresetFlags.today ? quickDateActiveRing : ""}`}
                                >
                                    {getTranslation("common.today", language) || "Today"}
                                </Button>
                                <Button
                                    variant={historyQuickPresetFlags.yesterday ? "primary" : "secondary"}
                                    size="sm"
                                    type="button"
                                    onClick={handleHistoryQuickYesterday}
                                    className={`!py-1.5 text-xs ${historyQuickPresetFlags.yesterday ? quickDateActiveRing : ""}`}
                                >
                                    {getTranslation("common.yesterday", language) || "Yesterday"}
                                </Button>
                                <Button
                                    variant={historyQuickPresetFlags.thisWeek ? "primary" : "secondary"}
                                    size="sm"
                                    type="button"
                                    onClick={handleHistoryQuickThisWeek}
                                    className={`!py-1.5 text-xs ${historyQuickPresetFlags.thisWeek ? quickDateActiveRing : ""}`}
                                >
                                    {getTranslation("common.thisWeek", language) || "This Week"}
                                </Button>
                                <Button
                                    variant={historyQuickPresetFlags.thisMonth ? "primary" : "secondary"}
                                    size="sm"
                                    type="button"
                                    onClick={handleHistoryQuickThisMonth}
                                    className={`!py-1.5 text-xs ${historyQuickPresetFlags.thisMonth ? quickDateActiveRing : ""}`}
                                >
                                    {getTranslation("common.thisMonth", language) || "This Month"}
                                </Button>
                                <button
                                    type="button"
                                    onClick={() => setHistoryDatePickerOpen(true)}
                                    className="inline-flex min-h-[36px] max-w-full min-w-0 items-center gap-1.5 rounded-md border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#161b22] px-2.5 py-1.5 text-xs font-medium text-gray-800 dark:text-[#c9d1d9] transition-colors hover:bg-gray-50 dark:hover:bg-[#21262d]"
                                    aria-label={getTranslation("payments.selectDate", language)}
                                >
                                    <img src={calendarIcon} alt="" className="h-3.5 w-3.5 shrink-0 opacity-80 dark:invert dark:opacity-80" />
                                    <span className="truncate tabular-nums">
                                        {historyDateFrom && historyDateTo
                                            ? `${historyDateFrom} – ${historyDateTo}`
                                            : getTranslation("payments.selectDate", language)}
                                    </span>
                                </button>
                            </div>
                            {hasActiveListFilters && (
                                <div className="flex w-full shrink-0 justify-end sm:ml-auto sm:w-auto sm:border-l sm:border-gray-200 sm:pl-4 dark:sm:border-[#30363d]">
                                    <ResetFiltersButton onClick={handleResetFilters} label={resetFiltersLabel} />
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                            <span className="text-xs font-medium text-gray-600 dark:text-[#8b949e] shrink-0">
                                {getTranslation("orderPage.deliveryTimeRange", language)}
                            </span>
                            <div
                                className="flex flex-wrap gap-1 min-w-0 flex-1"
                                role="group"
                                aria-label={getTranslation("orderPage.deliveryTimeRange", language)}
                            >
                                {historyTimeRangeOptions.map((opt) => (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => handleHistoryTimeRangeChange(opt.id)}
                                        className={`min-w-0 px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                                            historyTimeRange === opt.id
                                                ? "bg-gray-900 text-white border-gray-900 dark:bg-[#30363d] dark:text-[#c9d1d9] dark:border-[#58a6ff]/50 ring-1 ring-gray-400/30 dark:ring-[#58a6ff]/25"
                                                : "bg-white dark:bg-[#161b22] text-gray-800 dark:text-[#c9d1d9] border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#21262d]"
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                    </div>
                )}

            </div>

            <div className="mt-4">
                {!loading && !error && activeTab === "ongoing" && (
                    <NewOrdersBanner count={newOrdersCount} onRefresh={() => fetchOrders({ force: true })} />
                )}
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <LoadingSpinner />
                    </div>
                ) : error ? (
                    <ErrorDisplay
                        error={error}
                        onRetry={() => fetchOrders({ force: true })}
                    />
                ) : activeTab === "ongoing" && ongoingSubTab === "dishes" ? (
                    <>
                        {(totalCount > 0 || orderData.length > 0) && (
                            <div className="flex justify-end items-center mb-2 px-2">
                                <span className="bg-white dark:bg-[#161b22] text-gray-700 dark:text-[#c9d1d9] font-bold px-3 py-1 rounded-lg text-sm border dark:border-[#30363d]">
                                    {getTranslation("common.total", language)}: {totalCount}
                                </span>
                            </div>
                        )}
                        <KitchenSummary key={kitchenHighlightMountKey} orders={orderData} />

                        {totalCount > 0 && (
                            <div className="flex flex-col sm:flex-row justify-between items-center mt-6 mb-12 px-2">
                                <div className="mb-4 sm:mb-0">
                                    <RowsPerPageSelect
                                        label={getTranslation("common.rowsPerPage", language) || "Rows per page"}
                                        value={itemsPerPage}
                                        onChange={setItemsPerPageAndReset}
                                        margin=""
                                    />
                                </div>

                                <Pagination
                                    totalItems={totalCount}
                                    itemsPerPage={itemsPerPage}
                                    currentPage={currentPage}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {(totalCount > 0 || orderData.length > 0) && (
                            <div className="flex justify-end items-center mb-2 px-2">
                                <span className="bg-white dark:bg-[#161b22] text-gray-700 dark:text-[#c9d1d9] font-bold px-3 py-1 rounded-lg text-sm border dark:border-[#30363d]">
                                    {getTranslation("common.total", language)}: {totalCount}
                                </span>
                            </div>
                        )}

                        <OrderList
                            orders={orderData}
                            loading={loading}
                            activeTab={activeTab}
                            selectOptions={selectOptions}
                            selectedOptions={selectedOptions}
                            onStatusChange={handleSelectChange}
                            onViewOrder={handleViewOrder}
                            getOptionStyles={getOptionStyles}
                        />

                        {totalCount > 0 && (
                            <div className="flex flex-col sm:flex-row justify-between items-center mt-6 mb-12 px-2">
                                <div className="mb-4 sm:mb-0">
                                    <RowsPerPageSelect
                                        label={getTranslation("common.rowsPerPage", language) || "Rows per page"}
                                        value={itemsPerPage}
                                        onChange={setItemsPerPageAndReset}
                                        margin=""
                                    />
                                </div>

                                <Pagination
                                    totalItems={totalCount}
                                    itemsPerPage={itemsPerPage}
                                    currentPage={currentPage}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Modals */}
            {isModalVisible && (
                <ConfirmationModal
                    description={getTranslation("orderPage.declineOrderConfirmation", language)}
                    onCancel={handleCancelDecline} 
                    onSubmit={handleConfirmDecline}
                />
            )}

            {isOrderModalVisible && (
                <OrderModal closeModal={handleCloseOrderModal} orderId={declinedOrderId} />
            )}
            
            {/* Scroll to top button */}
            {showScrollTop && (
                <Button
                    variant="primary"
                    size="sm"
                    onClick={scrollToTop}
                    className="fixed bottom-4 right-4 w-10 h-10 rounded-full shadow-lg z-20 sm:hidden"
                    aria-label={getTranslation("common.scrollToTop", language) || "Scroll to top"}
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                </Button>
            )}
            <Modal
                isOpen={historyDatePickerOpen}
                onClose={() => setHistoryDatePickerOpen(false)}
                title={getTranslation("payments.selectDate", language)}
                size="4xl"
            >
                <DoubleCalendar
                    onClose={() => setHistoryDatePickerOpen(false)}
                    onDateSelect={handleHistoryCalendarSelect}
                    initialDateFrom={historyCalendarInitialFrom}
                    initialDateTo={historyCalendarInitialTo}
                />
            </Modal>

            <Modal
                isOpen={ongoingDatePickerOpen}
                onClose={() => setOngoingDatePickerOpen(false)}
                title={getTranslation("payments.selectDate", language)}
                size="4xl"
            >
                <DoubleCalendar
                    onClose={() => setOngoingDatePickerOpen(false)}
                    onDateSelect={handleOngoingCalendarSelect}
                    initialDateFrom={ongoingCalendarInitialFrom}
                    initialDateTo={ongoingCalendarInitialTo}
                    showQuickSelect={false}
                />
            </Modal>

            <Modal
                isOpen={historyListFiltersModalOpen}
                onClose={closeHistoryListFiltersModal}
                title={listFiltersTitle}
                size="lg"
            >
                <div className="flex flex-col max-h-[min(70vh,560px)] min-h-0">
                    <div className="overflow-y-auto flex-1 space-y-4 min-h-0 pb-2">
                        <div className="min-w-0">
                            <CustomMultiSelect
                                label={getTranslation("orderPage.status", language) || "Status"}
                                options={historyFilterOptions}
                                value={draftStatusFilter}
                                onChange={setDraftStatusFilter}
                                margin=""
                                placeholder={getTranslation("orderStatuses.all", language) || "All"}
                                isRequired={false}
                                showRequiredIndicator={false}
                                portalMenu
                            />
                        </div>
                        <div className="min-w-0">
                            <CustomMultiSelect
                                label={getTranslation("orderPage.paymentStatus", language) || "Payment status"}
                                options={paymentFilterOptions}
                                value={draftPaymentStatusFilter}
                                onChange={setDraftPaymentStatusFilter}
                                margin=""
                                placeholder={getTranslation("orderStatuses.all", language) || "All"}
                                isRequired={false}
                                showRequiredIndicator={false}
                                portalMenu
                            />
                        </div>
                        {showCompanyFilter && (
                        <div className="min-w-0">
                            <CustomMultiSelect
                                label={getTranslation("orderPage.company", language) || "Company"}
                                options={companyFilterOptions}
                                value={draftCompanyFilter}
                                onChange={setDraftCompanyFilter}
                                margin=""
                                placeholder={getTranslation("orderPage.allCompanies", language) || "All Companies"}
                                isRequired={false}
                                showRequiredIndicator={false}
                                portalMenu
                            />
                        </div>
                        )}
                    </div>
                    <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2 pt-4 mt-1 border-t border-gray-200 dark:border-[#30363d]">
                        <Button
                            type="button"
                            variant="primary"
                            className="w-full sm:flex-1"
                            onClick={applyHistoryListFilters}
                        >
                            {applyFiltersLabel}
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            className="w-full sm:flex-1"
                            onClick={resetAndApplyHistoryListFilters}
                        >
                            {resetAllFiltersLabel}
                        </Button>
                    </div>
                </div>
            </Modal>

            <Toast ref={toastRef} />
        </div>
    );
};

export default Orders;
